import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  PERMISSION_KEYS, PERMISSION_LABELS, PERMISSION_TABS, normalizePermissions, rowKeys, tabKeys,
  type PermissionKey, type PermissionRow, type UserDto, type UserPermissionsDto
} from "@delta/shared";
import { useLang } from "../i18n";
import { useSession } from "../lib/session";
import { api } from "../lib/api";
import { Icon } from "../components/Icon";
import { PageHead, Shell } from "../components/Shell";
import { useToast } from "../components/Toast";
import { errorText } from "../lib/errors";
import { DemoLockNotice } from "../components/Demo";

// Settings › Users › Edit permission: the 190 audited switches in the seven audited tabs, ported from the
// prototype (erp-settings.js "/permission/:id") and saved with PUT /api/users/:id/permissions.

const TAB_LABEL: Record<string, string> = { invoices: "tabInvoices", warehouses: "tabWarehouses", accounts: "tabGeneralAccounts", assets: "tabFixedAssets", hr: "tabHr", settings: "tabSettings", reports: "tabReports" };

export function Permissions() {
  const { id = "" } = useParams();
  const { t, lang } = useLang();
  const s = useSession();
  const toast = useToast();
  const [user, setUser] = useState<UserDto | null>(null);
  const [saved, setSaved] = useState<Set<string>>(new Set());
  const [v, setV] = useState<Set<string>>(new Set());
  const [tab, setTab] = useState("invoices");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api<UserDto>("GET", `/users/${id}`), api<UserPermissionsDto>("GET", `/users/${id}/permissions`)]).then(([u, p]) => {
      setUser(u); setSaved(new Set(p.permissions)); setV(new Set(p.permissions));
    }, (e) => setError(errorText(t, e)));
  }, [id, t]);

  const admin = !!user?.administrator;
  const me = s.me!;
  // A non-administrator can only switch keys they hold themselves (the server enforces the same rule).
  const mine = (k: string) => me.user.administrator || me.permissions.includes(k);
  const locked = (k: string) => admin || !mine(k);
  const PL = (k: string) => PERMISSION_LABELS[k as PermissionKey]?.[lang] ?? k;
  const dirty = useMemo(() => PERMISSION_KEYS.some((k) => saved.has(k) !== v.has(k)), [saved, v]);

  function flip(k: string, on: boolean) {
    const x = new Set(v);
    if (on) x.add(k); else x.delete(k);
    // Without view, add / update / delete mean nothing: switching view off clears them, switching one on turns view on.
    for (const tb of PERMISSION_TABS) for (const r of tb.rows ?? []) {
      const deps = rowKeys(r).filter((d) => d !== r.view);
      if (k === r.view && !on) deps.forEach((d) => { if (mine(d)) x.delete(d); });
      if (deps.includes(k as PermissionKey) && on) x.add(r.view);
    }
    setV(x);
  }
  const setMany = (keys: string[], on: boolean) => {
    const x = new Set(v);
    keys.filter((k) => !locked(k)).forEach((k) => (on ? x.add(k) : x.delete(k)));
    setV(new Set(normalizePermissions(x)));
  };

  async function save() {
    setBusy(true);
    try {
      const r = await api<UserPermissionsDto>("PUT", `/users/${id}/permissions`, { permissions: [...v] });
      const n = PERMISSION_KEYS.filter((k) => saved.has(k) !== r.permissions.includes(k)).length;
      setSaved(new Set(r.permissions)); setV(new Set(r.permissions));
      toast(t("permsSaved", { x: user!.name, n }));
      if (id === me.user.id) await s.refresh();
    } catch (e) {
      toast(errorText(t, e));
    } finally {
      setBusy(false);
    }
  }

  const sw = (k: string, label?: string) => (
    <label className={`sw ${locked(k) ? "locked" : ""}`} title={PL(k)}>
      <input type="checkbox" data-k={k} checked={admin || v.has(k)} disabled={locked(k)} onChange={(e) => flip(k, e.target.checked)} aria-label={label ?? PL(k)} />
      <span className="sw-track"><span className="sw-dot" /></span>{label != null && <span className="sw-l">{label}</span>}
    </label>
  );
  const tb = PERMISSION_TABS.find((x) => x.key === tab)!;
  const cnt = (x: typeof tb) => { const ks = tabKeys(x); return `${admin ? ks.length : ks.filter((k) => v.has(k)).length}/${ks.length}`; };
  const onCount = admin ? PERMISSION_KEYS.length : PERMISSION_KEYS.filter((k) => v.has(k)).length;

  const rowView = (r: PermissionRow) => {
    const ks = rowKeys(r);
    const cell = (op: "add" | "update" | "delete") => (r.ops[op] ? <td className="c">{sw(r.ops[op]!)}</td> : <td className="c muted">–</td>);
    const allOn = ks.every((k) => admin || v.has(k));
    return (
      <tr key={r.view}>
        <td><b className="pm-name">{PL(r.view)}</b><div className="hint num" dir="ltr">{r.view}</div></td>
        <td className="c">{sw(r.view)}</td>{cell("add")}{cell("update")}{cell("delete")}
        <td>{r.extras.length ? r.extras.map((k) => <div key={k} className="pm-x">{sw(k, PL(k))}</div>) : <span className="muted">–</span>}</td>
        <td className="c"><button className="btn btn-xs btn-ghost" disabled={admin} onClick={() => setMany(ks, !allOn)} aria-label={`${t("allOn")} · ${PL(r.view)}`}><Icon name={allOn ? "x" : "check"} /></button></td>
      </tr>
    );
  };

  const body = error ? (
    <section className="card"><div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{error}</p></div></section>
  ) : !user ? (
    <section className="card"><div className="state-box"><p>{t("loading")}</p></div></section>
  ) : (
    <section className="card list-card">
      <div className="tabs pm-tabs" role="tablist">
        {PERMISSION_TABS.map((x) => (
          <button key={x.key} role="tab" aria-selected={x.key === tab} className={`tab ${x.key === tab ? "on" : ""}`} onClick={() => setTab(x.key)}>{t(TAB_LABEL[x.key]!)} <span className="count num">{cnt(x)}</span></button>
        ))}
      </div>
      <div className="ltb">
        <span className="hint">{admin ? <><Icon name="lock" /> {t("adminSeesAll")}</> : t("permHint")}</span><span className="spacer" />
        <button className="btn btn-sm" disabled={admin} onClick={() => setMany(tabKeys(tb), true)}>{t("tabAllOn")}</button>
        <button className="btn btn-sm" disabled={admin} onClick={() => setMany(tabKeys(tb), false)}>{t("tabAllOff")}</button>
      </div>
      {tb.groups ? (
        <div className="pm-groups">{tb.groups.map((g) => {
          const ks = PERMISSION_KEYS.filter((k) => k.startsWith(g.prefix));
          const allOn = ks.every((k) => admin || v.has(k));
          return (
            <div key={g.prefix} className="pm-group">
              <div className="pm-gh"><b>{t(g.label)}</b><button className="btn btn-xs btn-ghost" disabled={admin} onClick={() => setMany(ks, !allOn)}>{allOn ? t("clearAll") : t("allOn")}</button></div>
              {ks.map((k) => <div key={k} className="pm-li">{sw(k, PL(k))}</div>)}
            </div>
          );
        })}</div>
      ) : (
        <div className="table-wrap"><table className="tbl pm-tbl">
          <thead><tr><th>{t("feature")}</th><th className="c">{t("view")}</th><th className="c">{t("add")}</th><th className="c">{t("updateF")}</th><th className="c">{t("delete")}</th><th>{t("otherPerms")}</th><th className="c">{t("all")}</th></tr></thead>
          <tbody>{tb.rows!.map(rowView)}</tbody>
        </table></div>
      )}
    </section>
  );

  const bar = user && (
    <div className="action-bar">
      <div className="ab-stats">
        <div><div className="k">{t("user")}</div><div className="v">{user.name}</div></div>
        <div><div className="k">{t("permsOn")}</div><div className="v num" data-testid="perms-on">{onCount} / {PERMISSION_KEYS.length}</div></div>
        <div><div className="k">{t("status")}</div><div className="v">{dirty ? <span className="pill warn">{t("unsavedChanges")}</span> : <span className="pill good">{t("allSaved")}</span>}</div></div>
      </div>
      <div className="ab-actions">
        <button className="btn" disabled={!dirty || busy} onClick={() => setV(new Set(saved))}>{t("discardChanges")}</button>
        <button className="btn btn-primary" disabled={!dirty || admin || busy} onClick={() => void save()}><Icon name="check" />{busy ? t("saving") : t("savePerms")}</button>
      </div>
    </div>
  );

  return (
    <Shell navKey="/users-settings" bar={bar}>
      <DemoLockNotice />
      <PageHead crumbs={[[t("home"), "/home"], [t("users"), "/users-settings"], [user?.name ?? "…"], [t("editPermission")]]} title={t("editPermission")}
        badge={user && <span className={`pill ${admin ? "info" : ""}`}>{user.name}</span>} />
      {body}
    </Shell>
  );
}
